import React from 'react';
import { graphql } from 'gatsby';

import 'bootstrap/dist/css/bootstrap.min.css';

import Layout from '../components/layout';
import Seo from '../components/seo';
import Hero from '../components/Hero';

import '../style/index.scss';
import { faqs } from '../../content/faqs';
import { reviews } from '../../content/reviews';
import { Map } from '../components/Map';
import { Reviews } from '../sections/Reviews';

// Import new layout components
import SectionWrapper from '../components/layout/SectionWrapper';
import IntroductionWrapper from '../components/layout/IntroductionWrapper';
import CallToActionWrapper from '../components/layout/CallToActionWrapper';
import ImageWrapper from '../components/layout/ImageWrapper';
import ProcessList from '../components/layout/ProcessList';
import ProcessSection from '../components/layout/ProcessSection';
import {
  IntroductionSection,
  AboutMeSection,
  PublicationsList,
  ConferencesList,
  EducationList,
  AcademicProfileList,
  AffiliationsList,
  PricingDisplay,
  FAQDisplay
} from '../components/content';

// Import the extracted content
import { hero } from '../../content/hero';
import { pricing } from '../../content/pricing';

// Import simplified content helpers
import { intro, process, creds, about } from '../utils/contentHelpers';

const IndexPage = ({ data }) => {

  // Map conference images to avoid dynamic property access issues
  const conferenceImages = [
    data.conference1?.childImageSharp?.fluid,
    data.conference2?.childImageSharp?.fluid,
    data.conference3?.childImageSharp?.fluid
  ];

  return (
    <>
    <Layout page=''>
      <Seo
        title='Supporto e Sostegno Psicologico'
        description='presentazione del dott. Jacopo Biraschi, dei suoi studi accademici e delle sue passioni, breve descrizione dei suoi ambiti di intervento e supporto psicologico quali, ad esempio, ansia, depressione e disturbi di personalit&agrave;, indirizzi dei due studi in cui riceve a Milano'
      />
      
      {/* Hero Section - using extracted content */}
      <Hero data={data} heroContent={hero} />
      
      <SectionWrapper>
        {/* First Introduction Section - using extracted content */}
        <IntroductionSection 
          section={intro.problemAwareness()} 
          image={data.problemAwarenessImage.childImageSharp.fluid}
          imagePosition='right'
          showCTA={true}
        />
        
        {/* Second Introduction Section */}
        <IntroductionSection 
          section={intro.lifeTooShort()} 
          image={data.lifeTooShortImage.childImageSharp.fluid}
          imagePosition='left'
        />
        
        {/* Third Introduction Section */}
        <IntroductionSection 
          section={intro.changePossible()} 
          image={data.changePossibleImage.childImageSharp.fluid}
          imagePosition='right'
        />
        
        {/* Fourth Introduction Section */}
        <IntroductionSection 
          section={intro.imagineDifferent()}
        />
        
        {/* Center Image */}
        <IntroductionWrapper>
          <ImageWrapper
            fluid={data.centerImage.childImageSharp.fluid}
            size='normal'
          />
        </IntroductionWrapper>
        
        {/* Therapy Process Section - using extracted content */}
        <IntroductionWrapper>
          <h3>{process.title}</h3>
          <IntroductionWrapper>
            <h4>{process.typicalResults().title}</h4>
            <p>{process.typicalResults().content}</p>
            <Reviews content={reviews} />
            <CallToActionWrapper text={process.typicalResults().cta.text} />
          </IntroductionWrapper>
        </IntroductionWrapper>
        
        {/* Assessment Sections */}
        <ProcessList section={process.assessment()} />
        <ProcessList section={process.analysisPlanning()} wrapperClassName='' />
        <ProcessList section={process.personalizedPath()} wrapperClassName='' />
      </SectionWrapper>
      
      {/* Assessment Importance */}
      <ProcessSection section={process.assessmentImportance()} />
      
      {/* CBT Section */}
      <ProcessSection 
        section={process.cbtMethodology()} 
        className='introduction'
        showSubtitle={true}
      />
      
      {/* RFD Innovation */}
      <IntroductionWrapper>
        {process.rfdIntegration().paragraphs.map((paragraph, index) => (
          <p key={index}>{paragraph}</p>
        ))}
      </IntroductionWrapper>
      
      {/* Credentials Section - using extracted content */}
      <IntroductionWrapper>
        <h3>{creds.title}</h3>
        
        {/* Education */}
        <EducationList 
          education={creds.education().items}
          title={creds.education().title}
        />
        
        {/* Publications */}
        <PublicationsList 
          publications={creds.publications().items}
          title={creds.publications().title}
        />
        
        {/* Academic Profile */}
        <AcademicProfileList 
          profiles={creds.academicProfile().items}
          title={creds.academicProfile().title}
        />
      </IntroductionWrapper>
      
      {/* Conference Sections - using extracted content */}
      <ConferencesList 
        conferences={creds.conferences().items}
        images={conferenceImages}
        title={creds.conferences().title}
      />
      
      {/* Affiliations */}
      <AffiliationsList 
        affiliations={creds.affiliations().items}
        title={creds.affiliations().title}
      />
      
      {/* Pricing Section - using extracted content */}
      <PricingDisplay pricing={pricing} />
      
      {/* Location & FAQ - already using extracted content */}
      <IntroductionWrapper>
        <h3>Localizzazione</h3>
        <Map />
      </IntroductionWrapper>
      
      <FAQDisplay faqs={faqs} />
      
      {/* About Me Section - using extracted content */}
      <IntroductionWrapper>
        <div>
          <h3>{about.title}</h3>
          <p>
            <strong>{about.concreteSolutions().title}</strong>{' '}
            {about.concreteSolutions().content}
          </p>
        </div>
        
        <AboutMeSection 
          differentiators={[
            about.customTreatment(),
            about.scientificTechniques()
          ]}
          image={data.aboutMeImage1.childImageSharp.fluid}
          imagePosition='left'
        />
        
        <SectionWrapper>
          <AboutMeSection 
            differentiators={[
              about.patientCare(),
              about.researchActive()
            ]}
            image={data.aboutMeImage2.childImageSharp.fluid}
            imagePosition='right'
          />
        </SectionWrapper>
      </IntroductionWrapper>
      
      {/* Personal Introduction - using extracted content */}
      <IntroductionWrapper extraMargin={true}>
        <div className='with-pic'>
          <ImageWrapper
            fluid={data.personalIntroImage.childImageSharp.fluid}
            size='big'
            wrapper={true}
          />
          <p>
            {about.personalIntro.greeting}
            <br />
            {about.personalIntro.description}
            <br />
            {about.personalIntro.invitation}
          </p>
        </div>
      </IntroductionWrapper>
      
      <CallToActionWrapper text={about.personalIntro.cta.text} />
    </Layout>
  </>
  );
};

export default IndexPage;

// Keep the exact same GraphQL query
export const query = graphql`
  query {
    heroMainImage: file(
      relativePath: { eq: "lina-trochez-ktPKyUs3Qjs-unsplash.jpg" }
    ) {
      childImageSharp {
        fluid(maxWidth: 1200, quality: 90) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    problemAwarenessImage: file(
      relativePath: { eq: "noah-silliman-gzhyKEo_cbU-unsplash_quad.jpg" }
    ) {
      childImageSharp {
        fluid(maxWidth: 1200, quality: 90) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    lifeTooShortImage: file(
      relativePath: { eq: "dev-asangbam-_sh9vkVbVgo-unsplash_quad.jpg" }
    ) {
      childImageSharp {
        fluid(maxWidth: 1200, quality: 90) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    changePossibleImage: file(
      relativePath: { eq: "pablo-heimplatz-EAvS-4KnGrk-unsplash_quad.jpg" }
    ) {
      childImageSharp {
        fluid(maxWidth: 1200, quality: 90) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    centerImage: file(relativePath: { eq: "marc-najera-Cj2d2IUEPDM-unsplash.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1200, quality: 90) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    personalIntroImage: file(relativePath: { eq: "dott_jacopo_biraschi_psicologo.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1200, quality: 90) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    rhoMaps: file(
      relativePath: { eq: "green-chameleon-s9CC2SKySJM-unsplash.jpg" }
    ) {
      childImageSharp {
        fluid(maxWidth: 1200, quality: 90) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    milanMaps: file(
      relativePath: { eq: "samantha-gades-BlIhVfXbi9s-unsplash.jpg" }
    ) {
      childImageSharp {
        fluid(maxWidth: 1200, quality: 90) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    womanTablet: file(
      relativePath: { eq: "brooke-cagle-uWVWQ8gF8PE-unsplash.jpg" }
    ) {
      childImageSharp {
        fluid(maxWidth: 1200, quality: 90) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    aboutMeImage1: file(
      relativePath: { eq: "getty-images-zf_eiBeRhhc-unsplash-square.jpg" }
    ) {
      childImageSharp {
        fluid(maxWidth: 1200, quality: 90) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    aboutMeImage2: file(
      relativePath: { eq: "galina-nelyubova-d3P2kQJIxqc-unsplash-square.jpg" }
    ) {
      childImageSharp {
        fluid(maxWidth: 1200, quality: 90) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    conference1: file(relativePath: { eq: "convegno1.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1200, quality: 90) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    conference2: file(relativePath: { eq: "convegno2.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1200, quality: 90) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    conference3: file(relativePath: { eq: "convegno3.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1200, quality: 90) {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`;
